/** Pieces shared by the quote pages (proposal §4). */
import { useQuery } from '@tanstack/react-query';
import {
  formatDecimal,
  type LicenceLight,
  type LineType,
  offerLabel,
  phaseOf,
  type QuoteLookups,
  QUOTE_STATE_LABELS,
  type QuoteState,
  type SubjectListItem,
} from '@k-management/shared';
import { apiRequest } from '../../lib/api.js';

export const quoteKeys = {
  all: ['quotes'] as const,
  lookups: ['quotes', 'lookups'] as const,
  quote: (id: string) => ['quotes', id] as const,
  history: (id: string) => ['quotes', id, 'history'] as const,
};

export function useQuoteLookups() {
  return useQuery({
    queryKey: quoteKeys.lookups,
    queryFn: () => apiRequest<QuoteLookups>('/quotes/lookups'),
    staleTime: 60_000,
  });
}

export interface SubjectOption {
  id: string;
  name: string;
  city: string | null;
  vatNumber: string | null;
  taxCode: string | null;
  /** Has an active Cliente role: a quote can be addressed to it. */
  isClient: boolean;
  /** Has an active Fornitore role: a document of the ciclo passivo can come from it (1.59). */
  isSupplier: boolean;
  /** Active and with a tax identifier: a quote can be issued to it. */
  usable: boolean;
  /** Name, aliases and tax identifiers, lower case: what the search box matches. */
  search: string;
}

/**
 * The subjects the user sees, ready for the search box. Suppliers are in there
 * too: finding them stops a duplicate being entered for a subject that is
 * already in the archive with another role.
 */
export function useSubjectSearch(enabled: boolean) {
  return useQuery({
    queryKey: ['subjects', 'search'],
    queryFn: async () => {
      const subjects = await apiRequest<SubjectListItem[]>('/subjects');
      return subjects
        .map((subject): SubjectOption => ({
          id: subject.id,
          name: subject.name,
          city: subject.city,
          vatNumber: subject.vatNumber,
          taxCode: subject.taxCode,
          isClient: subject.roles.some((role) => role.roleCode === 'C' && role.active),
          isSupplier: subject.roles.some((role) => role.roleCode === 'F' && role.active),
          usable: subject.active && subject.complete,
          search: [subject.name, ...subject.aliases, subject.vatNumber, subject.taxCode]
            .filter(Boolean)
            .join(' ')
            .toLowerCase(),
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'it'));
    },
    enabled,
    staleTime: 60_000,
  });
}

const STATE_CLASS: Record<QuoteState, string> = {
  bozza: 'muted',
  da_inviare: 'brand',
  inviata: 'info',
  accettata: 'ok',
  rifiutata: 'warn',
  annullata: 'muted',
  superata: 'muted',
};

/** The state of a quote; an open offer past its validity says «Scaduta». */
export function QuoteStatePill({
  state,
  expired = false,
}: {
  state: QuoteState;
  expired?: boolean;
}) {
  if (expired) return <span className="pill warn">Scaduta</span>;
  return <span className={`pill ${STATE_CLASS[state]}`}>{QUOTE_STATE_LABELS[state]}</span>;
}

/** Preventivo (internal) or offerta (for the client): the label of the phase (rule of 03/10/2026). */
export function PhaseTag({ state }: { state: QuoteState }) {
  return phaseOf(state) === 'preventivo' ? (
    <span className="phase-tag preventivo">Preventivo</span>
  ) : (
    <span className="phase-tag offerta">Offerta</span>
  );
}

interface Labelled {
  number: string | null;
  version?: number | null;
  versionOf?: { number: string; version: number } | null;
  referenceName: string | null;
}

/**
 * How a quote is cited: the offer with its version (OFF-2026-0001 v2), the
 * new version a preventivo will be, or its subject while it has none.
 */
export function quoteLabel(quote: Labelled): string {
  if (quote.number) return offerLabel(quote.number, quote.version ?? null);
  if (quote.versionOf)
    return `${offerLabel(quote.versionOf.number, quote.versionOf.version)} in preparazione`;
  return quote.referenceName ?? 'Preventivo senza oggetto';
}

/** Description of an entry of a lookup list (units, referents…), or its code. */
export function entryLabel(
  entries: { code: string; description: string }[] | undefined,
  code: string,
): string {
  return entries?.find((entry) => entry.code === code)?.description ?? code;
}

/** Description of a voice of the table Tipologie di riga, or its code. */
export function typeLabel(types: LineType[] | undefined, code: string | null): string {
  if (!code) return '';
  return types?.find((type) => type.code === code)?.description ?? code;
}

/**
 * Traffic light of a licence line against the price list (decision of
 * 26/09/2026): «a listino» when not discounted, else the discount.
 */
export function LicenceLightBadge({ light }: { light: LicenceLight }) {
  const words = { green: 'verde', yellow: 'giallo', red: 'rosso' } as const;
  return (
    <span className={`margin-light ${light.light}`} title={`Semaforo ${words[light.light]}`}>
      <span className="dot" aria-hidden="true" />
      {light.discount <= 0 ? 'a listino' : `−${formatDecimal(light.discount, 1, 1)} %`}
    </span>
  );
}

/** Where a quote lives (05/10/2026): a preventivo under «Preventivi», an offer under «Offerte». */
export function quotePath(quote: { id: string; state: QuoteState }): string {
  return `${phaseOf(quote.state) === 'preventivo' ? '/preventivatore' : '/offerte'}/${quote.id}`;
}
